import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { FieldPath } from 'react-hook-form';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import {
  getOrgPreferenceKey,
  PanelPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { CompanyFundMetricsDataModel } from '../../../schemas/CompanyFundMetrics.schema';
import { PreferencesServiceV3 } from '../../../util/preferences-service-v3';
import { useAllCompanyDetailsFields } from '../../CompanyProfiles2/Summary/useCompanyDetailsFields';
import { useAllInvestmentMetricsFields } from '../../CompanyProfiles2/Summary/useInvestmentMetricsFields';
import { getCompanyListsFieldsFromSchema } from '../../CompanyProfiles2/Summary/useCompanyListsFields';
import { ICompanyViewModel } from '../../../view-models/company.view-model';
import {
  portfolioDefaultViewIdState,
  selectedDetailsFieldsOrgState,
  selectedListOverviewFieldsOrgState,
  selectedMetricsFieldsOrgState,
} from './DataDisplayState';

export function useDetailsConfigActions() {
  const allFields = useAllCompanyDetailsFields();
  const [selectedFields, setSelectedFields] = useAtom(selectedDetailsFieldsOrgState);

  const handleReset = useCallback(async () => {
    const allFieldsKeys = allFields.map((field) => field.key as FieldPath<ICompanyDataModel>);
    setSelectedFields(allFieldsKeys);

    PreferencesServiceV3.get().setPreference<PanelPreferences>(
      PreferenceKey.companySummaryDetails,
      {
        selectedFields: allFieldsKeys,
      },
      PreferenceScope.ORGANIZATION
    );
  }, [allFields, setSelectedFields]);

  const handleSave = useCallback(async () => {
    PreferencesServiceV3.get().setPreference<PanelPreferences>(
      PreferenceKey.companySummaryDetails,
      {
        selectedFields: selectedFields ?? [],
      },
      PreferenceScope.ORGANIZATION
    );
  }, [selectedFields]);

  return { handleReset, handleSave };
}

export function useMetricsConfigActions() {
  const allFields = useAllInvestmentMetricsFields();
  const [selectedFields, setSelectedFields] = useAtom(selectedMetricsFieldsOrgState);

  const handleReset = useCallback(async () => {
    const allFieldsKeys = allFields.map((field) => field.key as FieldPath<CompanyFundMetricsDataModel>);
    setSelectedFields(allFieldsKeys);
  }, [allFields, setSelectedFields]);

  const handleSave = useCallback(async () => {
    // FIXME: Follow-up on MAGGIE-8172 to remove save as setting auto-saves
    setSelectedFields(selectedFields);
  }, [selectedFields, setSelectedFields]);

  return { handleReset, handleSave };
}

export function useListOverviewConfigActions() {
  const allFields = getCompanyListsFieldsFromSchema();
  const [selectedFields, setSelectedFields] = useAtom(selectedListOverviewFieldsOrgState);

  const handleReset = useCallback(async () => {
    const allFieldsKeys = allFields.map((field) => field.key as FieldPath<ICompanyViewModel>);
    setSelectedFields(allFieldsKeys);

    PreferencesServiceV3.get().setPreference<PanelPreferences>(
      PreferenceKey.companyListsOverview,
      {
        selectedFields: allFieldsKeys,
      },
      PreferenceScope.ORGANIZATION
    );
  }, [allFields, setSelectedFields]);

  const handleSave = useCallback(async () => {
    PreferencesServiceV3.get().setPreference<PanelPreferences>(
      PreferenceKey.companyListsOverview,
      {
        selectedFields: selectedFields ?? [],
      },
      PreferenceScope.ORGANIZATION
    );
  }, [selectedFields]);

  return { handleReset, handleSave };
}

export function usePortfolioConfigActions() {
  const [viewId, setViewId] = useAtom(portfolioDefaultViewIdState);

  const handleSave = useCallback(
    async (newViewId: number | null) => {
      if (newViewId !== null && newViewId <= 0) {
        throw new Error('View ID must be a valid positive number');
      }
      setViewId(newViewId);
      PreferencesServiceV3.get().setPreference<number | null>(
        getOrgPreferenceKey(PreferenceKey.portfolioLandingView, null),
        newViewId,
        PreferenceScope.ORGANIZATION
      );
    },
    [setViewId]
  );

  const handleClear = useCallback(async () => {
    setViewId(null);
    PreferencesServiceV3.get().setPreference<number | null>(
      getOrgPreferenceKey(PreferenceKey.portfolioLandingView, null),
      null,
      PreferenceScope.ORGANIZATION
    );
  }, [setViewId]);

  return { viewId, setViewId, handleSave, handleClear };
}
